/**
 * Transaction process graph for bookings:
 *   - default-booking
 */

/**
 * Transitions
 *
 * These strings must sync with values defined in Marketplace API,
 * since transaction objects given by API contain info about last transitions.
 * All the actions in API side happen in transitions,
 * so we need to understand what those strings mean.
 */

export const transitions = {
  // When a customer makes a booking to a listing, a transaction is
  // created with the initial request-payment transition.
  // At this transition a PaymentIntent is created by Marketplace API.
  // After this transition, the actual payment must be made on client-side directly to Stripe.
  REQUEST_PAYMENT: 'transition/request-payment',

  // A customer can also initiate a transaction with an inquiry, and
  // then transition that with a request.
  INQUIRE: 'transition/inquire',
  REQUEST_PAYMENT_AFTER_INQUIRY: 'transition/request-payment-after-inquiry',

  // Stripe SDK might need to ask 3D security from customer, in a separate front-end step.
  // Therefore we need to make another transition to Marketplace API,
  // to tell that the payment is confirmed.
  CONFIRM_PAYMENT: 'transition/confirm-payment',

  // If the payment is not confirmed in the time limit set in transaction process (by default 15min)
  // the transaction will expire automatically.
  EXPIRE_PAYMENT: 'transition/expire-payment',

  // Instant book path (boatersnetwork-booking only):
  // payment is captured immediately, bypassing provider approval.
  INSTANT_BOOK: 'transition/instant-book',
  INSTANT_BOOK_AFTER_INQUIRY: 'transition/instant-book-after-inquiry',
  EXPIRE_PAYMENT_INSTANT: 'transition/expire-payment-instant',
  CONFIRM_PAYMENT_INSTANT: 'transition/confirm-payment-instant',

  // When the provider accepts or declines a transaction from the
  // SalePage, it is transitioned with the accept or decline transition.
  ACCEPT: 'transition/accept',
  DECLINE: 'transition/decline',

  // The operator can accept or decline the offer on behalf of the provider
  OPERATOR_ACCEPT: 'transition/operator-accept',
  OPERATOR_DECLINE: 'transition/operator-decline',

  // The backend automatically expire the transaction.
  EXPIRE: 'transition/expire',

  // Customer can cancel before provider accepts (request-to-book only)
  CUSTOMER_CANCEL_BEFORE_ACCEPTANCE: 'transition/customer-cancel-before-acceptance',

  // Customer or operator can cancel an accepted booking
  CUSTOMER_CANCEL: 'transition/customer-cancel',

  // Provider can cancel an accepted booking (full refund to customer)
  PROVIDER_CANCEL: 'transition/provider-cancel',

  // Admin/operator can also cancel the transition.
  CANCEL: 'transition/cancel',

  // System timer — checked-in bookings auto-complete 1 day after booking end
  AUTO_COMPLETE_FROM_CHECKED_IN: 'transition/auto-complete-from-checked-in',

  // Provider or operator checks in the customer on day of rental
  CHECK_IN: 'transition/check-in',
  OPERATOR_CHECK_IN: 'transition/operator-check-in',

  // Provider or operator marks the boat as returned — starts deposit hold window
  MARK_RETURNED: 'transition/mark-returned',
  OPERATOR_MARK_RETURNED: 'transition/operator-mark-returned',

  // Deposit release transitions (returned → delivered)
  RELEASE_DEPOSIT: 'transition/release-deposit',
  OPERATOR_RELEASE_DEPOSIT: 'transition/operator-release-deposit',
  AUTO_RELEASE_DEPOSIT: 'transition/auto-release-deposit',

  // Provider reports damage during hold window
  REPORT_DAMAGE: 'transition/report-damage',

  // Operator resolves a damage report
  OPERATOR_CAPTURE_DEPOSIT: 'transition/operator-capture-deposit',
  OPERATOR_DISMISS_DAMAGE: 'transition/operator-dismiss-damage',

  // The backend will mark the transaction completed.
  COMPLETE: 'transition/complete',
  OPERATOR_COMPLETE: 'transition/operator-complete',

  // Reviews are given through transaction transitions. Review 1 can be
  // by provider or customer, and review 2 will be the other party of
  // the transaction.
  REVIEW_1_BY_PROVIDER: 'transition/review-1-by-provider',
  REVIEW_2_BY_PROVIDER: 'transition/review-2-by-provider',
  REVIEW_1_BY_CUSTOMER: 'transition/review-1-by-customer',
  REVIEW_2_BY_CUSTOMER: 'transition/review-2-by-customer',
  EXPIRE_CUSTOMER_REVIEW_PERIOD: 'transition/expire-customer-review-period',
  EXPIRE_PROVIDER_REVIEW_PERIOD: 'transition/expire-provider-review-period',
  EXPIRE_REVIEW_PERIOD: 'transition/expire-review-period',

  // Modification flow (v12, shipped Push 7A.1/7A.2)
  CUSTOMER_REQUEST_MODIFICATION: 'transition/customer-request-modification',
  PROVIDER_REQUEST_MODIFICATION: 'transition/provider-request-modification',
  CUSTOMER_ACCEPT_MODIFICATION: 'transition/customer-accept-modification',
  PROVIDER_ACCEPT_MODIFICATION: 'transition/provider-accept-modification',
  CUSTOMER_DECLINE_MODIFICATION: 'transition/customer-decline-modification',
  PROVIDER_DECLINE_MODIFICATION: 'transition/provider-decline-modification',
  CUSTOMER_WITHDRAW_MODIFICATION: 'transition/customer-withdraw-modification',
  PROVIDER_WITHDRAW_MODIFICATION: 'transition/provider-withdraw-modification',
  EXPIRE_MODIFICATION_24H: 'transition/expire-modification-24h',
  EXPIRE_MODIFICATION_AT_BOOKING_START: 'transition/expire-modification-at-booking-start',
};

/**
 * States
 *
 * These constants are only for making it clear how transitions work together.
 * You should not use these constants outside of this file.
 *
 * Note: these states are not in sync with states used transaction process definitions
 *       in Marketplace API. Only last transitions are passed along transaction object.
 */
export const states = {
  INITIAL: 'initial',
  INQUIRY: 'inquiry',
  PENDING_PAYMENT: 'pending-payment',
  PENDING_PAYMENT_INSTANT: 'pending-payment-instant',
  PAYMENT_EXPIRED: 'payment-expired',
  PREAUTHORIZED: 'preauthorized',
  DECLINED: 'declined',
  ACCEPTED: 'accepted',
  // v12 (Push 7A.1): intermediate state during a modification request
  MODIFICATION_PENDING: 'modification-pending',
  EXPIRED: 'expired',
  CANCELED: 'canceled',
  CANCELLED: 'cancelled',
  CHECKED_IN: 'checked-in',
  RETURNED: 'returned',
  DAMAGE_REPORTED: 'damage-reported',
  DELIVERED: 'delivered',
  REVIEWED: 'reviewed',
  REVIEWED_BY_CUSTOMER: 'reviewed-by-customer',
  REVIEWED_BY_PROVIDER: 'reviewed-by-provider',
};

/**
 * Description of transaction process graph
 *
 * You should keep this in sync with transaction process defined in Marketplace API
 *
 * Note: we don't use yet any state machine library,
 *       but this description format is following Xstate (FSM library)
 *       https://xstate.js.org/docs/
 */
export const graph = {
  // id is defined only to support Xstate format.
  // However if you have multiple transaction processes defined,
  // it is best to keep them in sync with transaction process aliases.
  id: 'default-booking/release-1',

  // This 'initial' state is a starting point for new transaction
  initial: states.INITIAL,

  // States
  states: {
    [states.INITIAL]: {
      on: {
        [transitions.INQUIRE]: states.INQUIRY,
        [transitions.REQUEST_PAYMENT]: states.PENDING_PAYMENT,
        [transitions.INSTANT_BOOK]: states.PENDING_PAYMENT_INSTANT,
      },
    },
    [states.INQUIRY]: {
      on: {
        [transitions.REQUEST_PAYMENT_AFTER_INQUIRY]: states.PENDING_PAYMENT,
        [transitions.INSTANT_BOOK_AFTER_INQUIRY]: states.PENDING_PAYMENT_INSTANT,
      },
    },

    [states.PENDING_PAYMENT]: {
      on: {
        [transitions.EXPIRE_PAYMENT]: states.PAYMENT_EXPIRED,
        [transitions.CONFIRM_PAYMENT]: states.PREAUTHORIZED,
      },
    },

    [states.PENDING_PAYMENT_INSTANT]: {
      on: {
        [transitions.EXPIRE_PAYMENT_INSTANT]: states.PAYMENT_EXPIRED,
        [transitions.CONFIRM_PAYMENT_INSTANT]: states.ACCEPTED,
      },
    },

    [states.PAYMENT_EXPIRED]: {},
    [states.PREAUTHORIZED]: {
      on: {
        [transitions.DECLINE]: states.DECLINED,
        [transitions.OPERATOR_DECLINE]: states.DECLINED,
        [transitions.EXPIRE]: states.EXPIRED,
        [transitions.ACCEPT]: states.ACCEPTED,
        [transitions.OPERATOR_ACCEPT]: states.ACCEPTED,
        [transitions.CUSTOMER_CANCEL_BEFORE_ACCEPTANCE]: states.CANCELLED,
      },
    },

    [states.DECLINED]: {},
    [states.EXPIRED]: {},
    [states.ACCEPTED]: {
      on: {
        [transitions.CUSTOMER_CANCEL]: states.CANCELLED,
        [transitions.CANCEL]: states.CANCELLED,
        [transitions.PROVIDER_CANCEL]: states.CANCELLED,
        [transitions.COMPLETE]: states.DELIVERED,
        [transitions.OPERATOR_COMPLETE]: states.DELIVERED,
        [transitions.CHECK_IN]: states.CHECKED_IN,
        [transitions.OPERATOR_CHECK_IN]: states.CHECKED_IN,
        [transitions.CUSTOMER_REQUEST_MODIFICATION]: states.MODIFICATION_PENDING,
        [transitions.PROVIDER_REQUEST_MODIFICATION]: states.MODIFICATION_PENDING,
      },
    },

    // v12 (Push 7A.1): all resolutions loop back to ACCEPTED. Expire-24h is
    // anchored to :time/first-entered-state, so repeated modifications on the
    // same tx share a single 24h clock.
    [states.MODIFICATION_PENDING]: {
      on: {
        [transitions.CUSTOMER_ACCEPT_MODIFICATION]: states.ACCEPTED,
        [transitions.PROVIDER_ACCEPT_MODIFICATION]: states.ACCEPTED,
        [transitions.CUSTOMER_DECLINE_MODIFICATION]: states.ACCEPTED,
        [transitions.PROVIDER_DECLINE_MODIFICATION]: states.ACCEPTED,
        [transitions.CUSTOMER_WITHDRAW_MODIFICATION]: states.ACCEPTED,
        [transitions.PROVIDER_WITHDRAW_MODIFICATION]: states.ACCEPTED,
        [transitions.EXPIRE_MODIFICATION_24H]: states.ACCEPTED,
        [transitions.EXPIRE_MODIFICATION_AT_BOOKING_START]: states.ACCEPTED,
      },
    },

    [states.CANCELED]: {},
    [states.CANCELLED]: {},

    [states.CHECKED_IN]: {
      on: {
        [transitions.AUTO_COMPLETE_FROM_CHECKED_IN]: states.DELIVERED,
        [transitions.MARK_RETURNED]: states.RETURNED,
        [transitions.OPERATOR_MARK_RETURNED]: states.RETURNED,
      },
    },

    [states.RETURNED]: {
      on: {
        [transitions.RELEASE_DEPOSIT]: states.DELIVERED,
        [transitions.OPERATOR_RELEASE_DEPOSIT]: states.DELIVERED,
        [transitions.AUTO_RELEASE_DEPOSIT]: states.DELIVERED,
        [transitions.REPORT_DAMAGE]: states.DAMAGE_REPORTED,
      },
    },

    [states.DAMAGE_REPORTED]: {
      on: {
        [transitions.OPERATOR_CAPTURE_DEPOSIT]: states.DELIVERED,
        [transitions.OPERATOR_DISMISS_DAMAGE]: states.DELIVERED,
      },
    },

    [states.DELIVERED]: {
      on: {
        [transitions.EXPIRE_REVIEW_PERIOD]: states.REVIEWED,
        [transitions.REVIEW_1_BY_CUSTOMER]: states.REVIEWED_BY_CUSTOMER,
        [transitions.REVIEW_1_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
      },
    },

    [states.REVIEWED_BY_CUSTOMER]: {
      on: {
        [transitions.REVIEW_2_BY_PROVIDER]: states.REVIEWED,
        [transitions.EXPIRE_PROVIDER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED_BY_PROVIDER]: {
      on: {
        [transitions.REVIEW_2_BY_CUSTOMER]: states.REVIEWED,
        [transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED]: { type: 'final' },
  },
};

// Check if a transition is the kind that should be rendered
// when showing transition history (e.g. ActivityFeed)
// The first transition and most of the expiration transitions made by system are not relevant
export const isRelevantPastTransition = transition => {
  return [
    transitions.ACCEPT,
    transitions.OPERATOR_ACCEPT,
    transitions.CANCEL,
    transitions.CUSTOMER_CANCEL,
    transitions.CUSTOMER_CANCEL_BEFORE_ACCEPTANCE,
    transitions.PROVIDER_CANCEL,
    transitions.COMPLETE,
    transitions.OPERATOR_COMPLETE,
    transitions.AUTO_COMPLETE_FROM_CHECKED_IN,
    transitions.CONFIRM_PAYMENT,
    transitions.CONFIRM_PAYMENT_INSTANT,
    transitions.DECLINE,
    transitions.OPERATOR_DECLINE,
    transitions.EXPIRE,
    transitions.CHECK_IN,
    transitions.OPERATOR_CHECK_IN,
    transitions.MARK_RETURNED,
    transitions.OPERATOR_MARK_RETURNED,
    transitions.RELEASE_DEPOSIT,
    transitions.OPERATOR_RELEASE_DEPOSIT,
    transitions.AUTO_RELEASE_DEPOSIT,
    transitions.REPORT_DAMAGE,
    transitions.OPERATOR_CAPTURE_DEPOSIT,
    transitions.OPERATOR_DISMISS_DAMAGE,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,

    // Modification flow (v12, Push 7A.1/7A.2) — shown in ActivityFeed
    transitions.CUSTOMER_REQUEST_MODIFICATION,
    transitions.PROVIDER_REQUEST_MODIFICATION,
    transitions.CUSTOMER_ACCEPT_MODIFICATION,
    transitions.PROVIDER_ACCEPT_MODIFICATION,
    transitions.CUSTOMER_DECLINE_MODIFICATION,
    transitions.PROVIDER_DECLINE_MODIFICATION,
    transitions.CUSTOMER_WITHDRAW_MODIFICATION,
    transitions.PROVIDER_WITHDRAW_MODIFICATION,
    transitions.EXPIRE_MODIFICATION_24H,
    transitions.EXPIRE_MODIFICATION_AT_BOOKING_START,
  ].includes(transition);
};

// Processes might be different on how reviews are handled.
// Default processes use two-sided diamond shape, where either party can make the review first
export const isCustomerReview = transition => {
  return [transitions.REVIEW_1_BY_CUSTOMER, transitions.REVIEW_2_BY_CUSTOMER].includes(transition);
};

// Processes might be different on how reviews are handled.
// Default processes use two-sided diamond shape, where either party can make the review first
export const isProviderReview = transition => {
  return [transitions.REVIEW_1_BY_PROVIDER, transitions.REVIEW_2_BY_PROVIDER].includes(transition);
};

// Check if the given transition is privileged.
//
// Privileged transitions need to be handled from a secure context,
// i.e. the backend. This helper is used to check if the transition
// should go through the local API endpoints, or if using JS SDK is
// enough.
export const isPrivileged = transition => {
  return [
    transitions.REQUEST_PAYMENT,
    transitions.REQUEST_PAYMENT_AFTER_INQUIRY,
    transitions.INSTANT_BOOK,
    transitions.INSTANT_BOOK_AFTER_INQUIRY,

    // Modification flow (v12): 8 actor transitions are :privileged? true in process.edn.
    // Routed through /api/request-modification, /api/respond-to-modification, and
    // /api/withdraw-modification; makeTransitionThunk never handles them today, so
    // this is for documentation/future-proofing. Expire transitions are system-driven
    // and not listed here.
    transitions.CUSTOMER_REQUEST_MODIFICATION,
    transitions.PROVIDER_REQUEST_MODIFICATION,
    transitions.CUSTOMER_ACCEPT_MODIFICATION,
    transitions.PROVIDER_ACCEPT_MODIFICATION,
    transitions.CUSTOMER_DECLINE_MODIFICATION,
    transitions.PROVIDER_DECLINE_MODIFICATION,
    transitions.CUSTOMER_WITHDRAW_MODIFICATION,
    transitions.PROVIDER_WITHDRAW_MODIFICATION,
  ].includes(transition);
};

// Check when transaction is completed (booking over)
export const isCompleted = transition => {
  const txCompletedTransitions = [
    transitions.COMPLETE,
    transitions.OPERATOR_COMPLETE,
    transitions.AUTO_COMPLETE_FROM_CHECKED_IN,
    transitions.RELEASE_DEPOSIT,
    transitions.OPERATOR_RELEASE_DEPOSIT,
    transitions.AUTO_RELEASE_DEPOSIT,
    transitions.OPERATOR_CAPTURE_DEPOSIT,
    transitions.OPERATOR_DISMISS_DAMAGE,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    transitions.EXPIRE_REVIEW_PERIOD,
    transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD,
    transitions.EXPIRE_PROVIDER_REVIEW_PERIOD,
  ];
  return txCompletedTransitions.includes(transition);
};

// Check when transaction is refunded (booking did not happen)
// In these transitions action/stripe-refund-payment is called
export const isRefunded = transition => {
  const txRefundedTransitions = [
    transitions.EXPIRE_PAYMENT,
    transitions.EXPIRE_PAYMENT_INSTANT,
    transitions.EXPIRE,
    transitions.CANCEL,
    transitions.CUSTOMER_CANCEL,
    transitions.CUSTOMER_CANCEL_BEFORE_ACCEPTANCE,
    transitions.PROVIDER_CANCEL,
    transitions.DECLINE,
  ];
  return txRefundedTransitions.includes(transition);
};

export const statesNeedingProviderAttention = [states.PREAUTHORIZED];
